import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { useData } from '@hooks/useData';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { EnumSortDirection } from '@hooks/useRequest';
import { IContratoCliente, useOrigemCancelamentoCombo } from '@model/ContratoCliente';
import { EnumContratoClienteStatus, useContratoClienteStatusCombo } from '@model/ContratoClienteStatus';
import { EnumEmpresa, useEmpresaCombo } from '@model/Empresa';
import { EnumOperadora, useOperadoraCombo } from '@model/Operadora';
import { IContratoClienteFilter, useContratoClienteService } from '@services/ContratoClienteService';
import React from 'react';
import { ContratoClienteFilter } from './ContratoClienteFilter';
import { ContratoClienteTable } from './ContratoClienteTable';
import { ContratoClienteCancelarModal } from './ContratoClienteCancelarModal';
import { ContratoClienteEditarModal } from './ContratoClienteEditarModal';
import { ContratoMenu } from './ContratoMenu';
import { useMenu } from '@hooks/useMenu';
import { useModalWindow } from '@hooks/useModalWindow';
import { AtividadeModal } from '@mod/Atividade/AtividadeModal';
import { ContratoClienteReativarModal } from './ContratoClienteReativarModal';
import { useRemetenteWhatsappCombo } from '@model/RemetenteWhatsapp';

export type ContratoClienteProps = {
}

export function ContratoCliente (props: ContratoClienteProps) {
    const data = useData<IContratoCliente, IContratoClienteFilter>({
        filter: {
            contratoClienteId: useField(),
            clienteNome: useField(),
            clienteCpf: useField(),
            linhaNumero: useField(),
            linhaContratoOperadoraEmpresaId: useField(EnumEmpresa.None),
            linhaContratoOperadoraOperadoraId: useField(EnumOperadora.None),
            contratoClienteStatusId: useField(EnumContratoClienteStatus.None),
            usuarioLinha: useField(),
            whatsappEnvio: useField()
        },

        sortField: {
            name: "clienteNome",
            direction: EnumSortDirection.Asc
        }
    })

    const loading = useLoading();
    const message = useMessage();
    const contratoClienteService = useContratoClienteService();

    const contratoClienteStatusCombo = useContratoClienteStatusCombo();
    const origemCancelamentoCombo = useOrigemCancelamentoCombo();
    const empresaCombo = useEmpresaCombo();
    const operadoraCombo = useOperadoraCombo();
    const remetenteWhatsappCombo = useRemetenteWhatsappCombo();

    const menu = useMenu();
    const commandLoading = useLoading();
    const cancelarModal = useModalWindow();
    const reativarModal = useModalWindow();
    const editarModal = useModalWindow();
    const atividadeModal = useModalWindow();
    
    React.useEffect(() => {
        if (!loading.isLoading) {
            loading.start();
            contratoClienteService.listar(data.requestedData)
                .then(data.handleResultResponse)
                .catch(message.showException)
                .finally(loading.stop);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.requestedData]);

    useEffectOnce(() => {
        contratoClienteStatusCombo.loadOptions();
        origemCancelamentoCombo.loadOptions();
        empresaCombo.loadOptions();
        operadoraCombo.loadOptions();
        remetenteWhatsappCombo.loadOptions();
    });

    return (
        <React.Fragment>
            <Main>
                <PageTitle>
                    <PageTitleText text="Contratos" />
                </PageTitle>

                <PageContent>
                    <ContratoClienteFilter
                        filter={data.filter}
                        isLoading={loading.isLoading}
                        contratoClienteStatusCombo={contratoClienteStatusCombo}
                        empresaCombo={empresaCombo}
                        operadoraCombo={operadoraCombo}
                        remetenteWhatsappCombo={remetenteWhatsappCombo}
                    />
                    
                    <ContratoClienteTable 
                        data={data}
                        isLoading={loading.isLoading}
                        menu={menu}
                    />
                </PageContent>
            </Main>


            {
                data.focusedRow && (
                    <React.Fragment>
                        <ContratoMenu
                            menu={menu}
                            contratoCliente={data.focusedRow}
                            cancelarModal={cancelarModal}
                            reativarModal={reativarModal}
                            editarModal={editarModal}
                            commandLoading={commandLoading}
                            atividadeModal={atividadeModal}
                        />

                        <ContratoClienteCancelarModal 
                            modal={cancelarModal}
                            contratoCliente={data.focusedRow}
                            origemCancelamentoCombo={origemCancelamentoCombo}
                            onContratoCancelado={data.handleUpdateFocusedRow}
                        />

                        <ContratoClienteReativarModal 
                            modal={reativarModal}
                            contratoCliente={data.focusedRow}
                            onContratoReativado={data.handleUpdateFocusedRow}
                        />

                        <ContratoClienteEditarModal 
                            modal={editarModal}
                            contratoCliente={data.focusedRow}
                            onContratoClienteEditado={data.handleUpdateFocusedRow}
                        />

                        <AtividadeModal 
                            modal={atividadeModal}
                            entidade='contratocliente'
                            chaves={{
                                contratoClienteId: data.focusedRow.contratoClienteId
                            }}
                        />
                    </React.Fragment>
                )
            }
        </React.Fragment>
    )
}